import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, MessageSquare, CheckCircle2, ArrowRight, Video, Lock, Camera, Send } from "lucide-react";
import { Section, Container, Eyebrow, DisplayH1 } from "@/components/Layout";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Admin() {
  const { user, loading } = useAuth();
  const [queue, setQueue] = useState([]);
  const [answering, setAnswering] = useState({});
  const [answers, setAnswers] = useState({});
  const [makePublic, setMakePublic] = useState({});
  const [videoDoc, setVideoDoc] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dogs, setDogs] = useState([]);
  const [update, setUpdate] = useState({ dog_id: "", title: "", body: "", photo_url: "" });
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/founder-qa").then((r) => setQueue(r.data.questions || []));
    api.get("/media/founder-video").then((r) => setVideoDoc(r.data?.url ? r.data : null)).catch(() => {});
    api.get("/foundation/dogs").then((r) => {
      const list = r.data.dogs || [];
      setDogs(list);
      setUpdate((u) => ({ ...u, dog_id: u.dog_id || list[0]?.dog_id || "" }));
    });
  }, [user]);

  const answer = async (qid) => {
    const text = (answers[qid] || "").trim();
    if (text.length < 4) { toast.error("Write a longer answer."); return; }
    setAnswering({ ...answering, [qid]: true });
    try {
      await api.post(`/admin/founder-qa/${qid}/answer`, {
        answer: text,
        make_public: makePublic[qid] ?? undefined,
      });
      toast.success("Published to the founder's desk.");
      setQueue((qs) => qs.map((q) => q.question_id === qid ? { ...q, status: "answered", answer: text, answered_at: new Date().toISOString(), is_public: makePublic[qid] ?? q.is_public } : q));
      setAnswers({ ...answers, [qid]: "" });
    } catch { toast.error("Could not publish."); }
    finally { setAnswering({ ...answering, [qid]: false }); }
  };

  const upload = async (e) => {
    e.preventDefault();
    const input = document.getElementById("founder-video-input");
    const file = input?.files?.[0];
    if (!file) { toast.error("Pick a video file."); return; }
    setUploading(true);
    setProgress(0);
    const form = new FormData();
    form.append("file", file);
    if (caption) form.append("caption", caption);
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${BACKEND_URL}/api/media/founder-video`);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
      };
      await new Promise((resolve, reject) => {
        xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(xhr.responseText));
        xhr.onerror = () => reject(new Error("upload failed"));
        xhr.send(form);
      });
      const r = await api.get("/media/founder-video");
      setVideoDoc(r.data);
      toast.success("Founder video published.");
    } catch (err) {
      toast.error("Upload failed. Only video files up to 500MB.");
    } finally { setUploading(false); setProgress(0); }
  };

  if (loading) return <Section className="pt-40 pb-40"><Container><div>Loading…</div></Container></Section>;
  if (!user) return (
    <Section className="pt-40 pb-40"><Container>
      <Eyebrow>Admin</Eyebrow>
      <DisplayH1 className="mt-6">Please sign in.</DisplayH1>
      <div className="mt-8"><Link className="btn-gold" to="/login">Sign in</Link></div>
    </Container></Section>
  );
  if (!user.is_admin) return (
    <Section className="pt-40 pb-40"><Container>
      <Lock className="text-[color:var(--wk-gold)]" />
      <Eyebrow className="mt-6">Founder-only</Eyebrow>
      <DisplayH1 className="mt-6 max-w-2xl">This panel is reserved for Toriano and the Wolfkeeper faculty.</DisplayH1>
      <p className="mt-6 text-[color:var(--wk-bone-dim)]">Signed in as <span className="text-[color:var(--wk-bone)]">{user.email}</span>. Contact admissions@wolfkeeper.edu for elevated access.</p>
      <div className="mt-8"><Link className="btn-ghost" to="/">Return home</Link></div>
    </Container></Section>
  );

  const queued = queue.filter((q) => q.status !== "answered");
  const answered = queue.filter((q) => q.status === "answered");

  return (
    <>
      <Section testid="admin-hero" className="pt-32 pb-10">
        <Container>
          <Eyebrow>Founder Studio</Eyebrow>
          <DisplayH1 className="mt-4">Welcome, {user.name?.split(" ")[0]}.</DisplayH1>
        </Container>
      </Section>

      {/* Founder Video Upload */}
      <Section testid="admin-video" className="pb-16">
        <Container>
          <div className="card-lux p-8 md:p-10">
            <div className="flex items-center gap-3">
              <Video className="text-[color:var(--wk-gold)]" />
              <div className="font-serif-display text-2xl">Founder Video</div>
            </div>
            {videoDoc?.url ? (
              <div className="mt-6 grid md:grid-cols-2 gap-8">
                <video
                  src={videoDoc.url.startsWith("http") ? videoDoc.url : `${BACKEND_URL}${videoDoc.url}`}
                  controls
                  className="w-full aspect-video rounded-2xl object-cover bg-black"
                />
                <div>
                  <div className="overline">Currently published</div>
                  <div className="mt-2 font-serif-display text-xl">{videoDoc.caption || "The Founder's Story"}</div>
                  <div className="mt-1 text-xs text-[color:var(--wk-bone-dim)]">
                    {(videoDoc.size_bytes / (1024 * 1024)).toFixed(1)} MB · {videoDoc.content_type}
                  </div>
                  <div className="mt-1 text-xs text-[color:var(--wk-bone-dim)]">Uploaded {videoDoc.uploaded_at?.slice(0, 10)} by {videoDoc.uploaded_by}</div>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-[color:var(--wk-bone-dim)]">No video uploaded yet. Drop your cinematic intro here — it will replace the fallback poster across the site.</p>
            )}
            <form onSubmit={upload} className="mt-8 flex flex-col md:flex-row gap-4 md:items-end">
              <div className="flex-1">
                <label className="overline">Caption</label>
                <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="The Founder's Story — In His Own Words" className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-3" />
              </div>
              <label className="btn-ghost cursor-pointer" data-testid="admin-video-picker">
                <UploadCloud size={16} /> Choose file
                <input id="founder-video-input" type="file" accept="video/*" className="hidden" />
              </label>
              <button disabled={uploading} className="btn-gold" data-testid="admin-video-upload">
                {uploading ? `Uploading ${progress}%…` : (videoDoc ? "Replace video" : "Upload video")}
              </button>
            </form>
            {uploading && (
              <div className="mt-4 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[color:var(--wk-gold)]" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        </Container>
      </Section>

      {/* Sponsor Impact Update composer */}
      <Section testid="admin-impact" className="pb-16">
        <Container>
          <div className="card-lux p-8 md:p-10">
            <div className="flex items-center gap-3">
              <Camera className="text-[color:var(--wk-gold)]" />
              <div className="font-serif-display text-2xl">Post an Impact Update</div>
            </div>
            <p className="mt-3 text-sm text-[color:var(--wk-bone-dim)]">Send a fresh note and photo to everyone sponsoring this dog. It appears on their dashboard instantly.</p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!update.dog_id || update.body.length < 4) { toast.error("Add a note first."); return; }
                setPosting(true);
                try {
                  await api.post(`/admin/foundation/dogs/${update.dog_id}/update`, {
                    title: update.title || `Update on ${dogs.find((d) => d.dog_id === update.dog_id)?.name}`,
                    body: update.body,
                    photo_url: update.photo_url || null,
                  });
                  toast.success("Update posted to sponsors.");
                  setUpdate({ ...update, title: "", body: "", photo_url: "" });
                } catch { toast.error("Could not post update."); }
                finally { setPosting(false); }
              }}
              className="mt-6 grid gap-4 md:grid-cols-2"
              data-testid="admin-impact-form"
            >
              <div>
                <label className="overline">Dog</label>
                <select value={update.dog_id} onChange={(e) => setUpdate({ ...update, dog_id: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-3 text-lg outline-none focus:border-[color:var(--wk-gold)]" data-testid="admin-impact-dog">
                  {dogs.map((d) => <option className="bg-[#0a0a0a]" key={d.dog_id} value={d.dog_id}>{d.name} — {d.breed}</option>)}
                </select>
              </div>
              <div>
                <label className="overline">Headline</label>
                <input value={update.title} onChange={(e) => setUpdate({ ...update, title: e.target.value })} placeholder={update.dog_id ? `e.g. ${dogs.find((d) => d.dog_id === update.dog_id)?.name}'s public access week` : "Headline"} className="w-full bg-transparent border-b border-white/20 py-3 text-lg outline-none focus:border-[color:var(--wk-gold)]" data-testid="admin-impact-title" />
              </div>
              <div className="md:col-span-2">
                <label className="overline">Note to sponsors</label>
                <textarea rows={4} value={update.body} onChange={(e) => setUpdate({ ...update, body: e.target.value })} placeholder="What happened this month? What did they achieve?" className="w-full bg-transparent border border-white/10 focus:border-[color:var(--wk-gold)] outline-none rounded-xl p-3 text-base resize-none" data-testid="admin-impact-body" />
              </div>
              <div className="md:col-span-2">
                <label className="overline">Photo URL (optional)</label>
                <input value={update.photo_url} onChange={(e) => setUpdate({ ...update, photo_url: e.target.value })} placeholder="https://…" className="w-full bg-transparent border-b border-white/20 py-3 outline-none focus:border-[color:var(--wk-gold)]" />
              </div>
              <div className="md:col-span-2 flex gap-3 items-center">
                <button disabled={posting} className="btn-gold" data-testid="admin-impact-submit">
                  <Send size={14} /> {posting ? "Sending…" : "Send to sponsors"}
                </button>
                <span className="text-xs text-[color:var(--wk-bone-dim)]">Sponsors will see this in their dashboard the next time they visit.</span>
              </div>
            </form>
          </div>
        </Container>
      </Section>

      {/* Q&A Queue */}
      <Section testid="admin-qa" className="pb-32">
        <Container>
          <div className="flex items-center justify-between mb-8">
            <div>
              <Eyebrow>Founder Q&amp;A · Queue</Eyebrow>
              <div className="mt-2 font-serif-display text-3xl">{queued.length} awaiting reply · {answered.length} answered</div>
            </div>
            <Link to="/wall" className="btn-ghost">View public wall <ArrowRight size={14} /></Link>
          </div>

          <div className="space-y-6">
            {queued.length === 0 && (
              <div className="card-lux p-8 text-[color:var(--wk-bone-dim)] text-center">No questions in the queue. Well done.</div>
            )}
            {queued.map((q) => (
              <div key={q.question_id} className="card-lux p-6 md:p-8" data-testid={`admin-q-${q.question_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="overline text-[color:var(--wk-gold)]">{q.topic || "General"} · {q.is_public ? "PUBLIC ok" : "PRIVATE"}</div>
                    <div className="mt-2 font-serif-display text-2xl leading-tight">"{q.question}"</div>
                    <div className="mt-2 text-sm text-[color:var(--wk-bone-dim)]">
                      — {q.user_name} · {q.created_at?.slice(0, 10)}
                    </div>
                  </div>
                  <MessageSquare className="text-[color:var(--wk-gold)] mt-1" />
                </div>
                <div className="mt-6">
                  <textarea
                    rows={4}
                    value={answers[q.question_id] || ""}
                    onChange={(e) => setAnswers({ ...answers, [q.question_id]: e.target.value })}
                    placeholder="Toriano's reply…"
                    data-testid={`admin-answer-${q.question_id}`}
                    className="w-full bg-transparent border border-white/10 focus:border-[color:var(--wk-gold)] outline-none rounded-xl p-4 text-base resize-none"
                  />
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-[color:var(--wk-bone-dim)] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={makePublic[q.question_id] ?? q.is_public}
                      onChange={(e) => setMakePublic({ ...makePublic, [q.question_id]: e.target.checked })}
                      className="accent-[color:var(--wk-gold)]"
                    />
                    Publish on the Founder's Wall
                  </label>
                  <button
                    onClick={() => answer(q.question_id)}
                    disabled={answering[q.question_id]}
                    className="btn-gold"
                    data-testid={`admin-publish-${q.question_id}`}
                  >
                    {answering[q.question_id] ? "Publishing…" : "Publish reply"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {answered.length > 0 && (
            <div className="mt-16">
              <Eyebrow>Recently answered</Eyebrow>
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                {answered.slice(0, 12).map((q) => (
                  <div key={q.question_id} className="card-lux p-6">
                    <div className="flex items-center justify-between">
                      <div className="overline text-[10px] text-[color:var(--wk-gold)]">{q.topic}</div>
                      <CheckCircle2 size={14} className="text-[color:var(--wk-gold)]" />
                    </div>
                    <div className="mt-2 font-serif-display text-lg leading-tight">"{q.question}"</div>
                    <div className="mt-2 text-xs text-[color:var(--wk-bone-dim)]">{q.answered_at?.slice(0, 10)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Container>
      </Section>
    </>
  );
}
